import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, CheckCircle, AlertTriangle, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { api } from '../../lib/api';

type RecoveryStep = 'initiate' | 'success';

export function Recovery() {
  const navigate = useNavigate();
  const [step, setStep] = useState<RecoveryStep>('initiate');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInitiate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await api.post('/auth/forgot-password', { email });
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 429) {
        setError(err.response?.data?.message || 'Muitas tentativas. Tente novamente mais tarde.');
        setLoading(false);
        return;
      }
    } finally {
      setLoading(false);
    }
    setStep('success');
  };

  const renderStep = () => {
    switch (step) {
      case 'initiate':
        return (
          <form onSubmit={handleInitiate} className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary-500/20 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-primary-400" />
              </div>
              <h2 className="text-xl font-semibold text-surface-100">Recuperar Conta</h2>
              <p className="text-surface-400 mt-2">
                Digite seu email para receber um link de redefinição de senha
              </p>
            </div>

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              required
              leftIcon={<Mail className="w-4 h-4" />}
            />

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar Link de Recuperação'
              )}
            </Button>
          </form>
        );

      case 'success':
        return (
          <div className="text-center space-y-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto"
            >
              <CheckCircle className="w-10 h-10 text-green-400" />
            </motion.div>

            <div>
              <h2 className="text-xl font-semibold text-surface-100">Email Enviado</h2>
              <p className="text-surface-400 mt-2">
                Se o email estiver cadastrado, enviaremos instruções para redefinir sua senha.
                Verifique sua caixa de entrada e spam.
              </p>
            </div>

            <Button onClick={() => navigate('/login')} className="w-full">
              Voltar para Login
            </Button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-surface-950 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <Card className="p-8">
          {step !== 'success' && (
            <button
              onClick={() => navigate('/login')}
              className="flex items-center gap-2 text-surface-400 hover:text-surface-300 mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para login
            </button>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-3"
            >
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm">{error}</p>
            </motion.div>
          )}

          {renderStep()}
        </Card>
      </motion.div>
    </div>
  );
}
